import { useState, useEffect, useTransition } from "react";

import styled from "styled-components";

import type { MonitorAndProtectModelCampaignsCampaignModel } from "@similarweb/pro-api-client";
import { Modal } from "@similarweb/ui-components";

import { useMonitorAndProtectCampaigns } from "app/pages/digital-marketing/monitor-and-protect/services/api/campaign";
import { campaignWizardTracking } from "app/pages/digital-marketing/monitor-and-protect/services/tracking/campaign-wizard";
import { ConfirmationModal } from "app/pages/digital-marketing/monitor-and-protect/shared/components/confirmation-modal";

import type { IImportedKeywords } from "../keywords-edit";

import { useCampaignWizardState } from "./hooks/use-campaign-wizard-state";
import { useCreateCampaignMutation } from "./hooks/use-create-campaign-mutation";
import {
    CountriesStep,
    CountriesStepHeader,
    CountriesStepFooter,
    KeywordsStep,
    KeywordsStepHeader,
    KeywordsStepFooter,
    CampaignNameStep,
    CampaignNameStepHeader,
    CampaignNameStepFooter,
} from "./steps";
import { CAMPAIGN_WIZARD_TRANSLATIONS } from "./translations";
import { generateCampaignNameFromDomain } from "./utils/campaign-name-generation";

interface ICreateCampaignWizardProps {
    isOpen: boolean;
    selectedDomain: string;
    onClose: () => void;
    onCampaignCreated?: (campaign: MonitorAndProtectModelCampaignsCampaignModel) => void | Promise<void>;
}

const WizardHeader = styled(Modal.Header)`
    padding-bottom: 16px;
`;

const WizardFooter = styled(Modal.Footer)`
    padding: 8px 24px;
    justify-content: space-between;
    align-items: center;
`;

export const CreateCampaignWizard = ({
    isOpen,
    selectedDomain,
    onClose,
    onCampaignCreated,
}: ICreateCampaignWizardProps) => {
    const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
    const [isPending, startTransition] = useTransition();
    const { state, keywords, validation, steps, actions } = useCampaignWizardState(selectedDomain);
    const { data: existingCampaigns = [] } = useMonitorAndProtectCampaigns();

    useEffect(() => {
        if (isOpen) {
            campaignWizardTracking.onWizardOpen();
        }
    }, [isOpen]);

    const mutation = useCreateCampaignMutation({
        selectedDomain,
        keywords: keywords.deduplicated.uniqueKeywords,
        locations: state.selectedCountries.map((country) => ({ countryCode: country })),
        campaignName: state.campaignName,
        onSuccess: (campaign) => {
            startTransition(async () => {
                await onCampaignCreated?.(campaign);
                actions.reset();
                onClose();
            });
        },
    });

    const handleKeywordsImport = (importedKeywords: IImportedKeywords) => {
        actions.addImport(importedKeywords);
    };

    const handleRemoveImport = (importId: string) => {
        actions.removeImport(importId);
    };

    const handleClose = () => {
        setShowCloseConfirmation(true);
    };

    const handleConfirmClose = () => {
        actions.reset();
        setShowCloseConfirmation(false);
        onClose();
    };

    const handleCancelClose = () => {
        setShowCloseConfirmation(false);
    };

    const handleDone = () => {
        campaignWizardTracking.onCampaignNameDone();
        mutation.mutate();
    };

    const handleCountriesNext = async () => {
        campaignWizardTracking.onNextStep1();
        await actions.fetchBrandedKeywords();
        actions.goToNext();
    };

    const handleKeywordsNext = () => {
        const existingNames = existingCampaigns.map((campaign) => campaign.name);
        const generatedName = generateCampaignNameFromDomain(selectedDomain, existingNames);
        actions.setCampaignName(generatedName);
        actions.goToNext();
    };

    return (
        <>
            <ConfirmationModal
                isOpen={showCloseConfirmation}
                title={CAMPAIGN_WIZARD_TRANSLATIONS.discardConfirmation.modalQuestion.translate()}
                confirmationText={CAMPAIGN_WIZARD_TRANSLATIONS.discardConfirmation.confirmButton.translate()}
                cancelText={CAMPAIGN_WIZARD_TRANSLATIONS.discardConfirmation.cancelButton.translate()}
                onConfirm={handleConfirmClose}
                onCancel={handleCancelClose}
            />
            <Modal
                isOpen={isOpen ? !showCloseConfirmation : null}
                customStyles={{ content: { width: steps.config?.width || "712px" } }}
            >
                <WizardHeader>
                    {state.currentStep === steps.constants.COUNTRIES ? (
                        <CountriesStepHeader
                            currentStep={steps.index}
                            totalSteps={2}
                        />
                    ) : state.currentStep === steps.constants.KEYWORDS ? (
                        <KeywordsStepHeader
                            currentStep={steps.index}
                            totalSteps={2}
                        />
                    ) : (
                        <CampaignNameStepHeader />
                    )}
                    <Modal.CloseButton onClick={handleClose} />
                </WizardHeader>
                <Modal.Content
                    gap="16px"
                    height={state.currentStep === steps.constants.CAMPAIGN_NAME ? "max-content" : "520px"}
                >
                    {state.currentStep === steps.constants.COUNTRIES ? (
                        <CountriesStep
                            selectedCountries={state.selectedCountries}
                            deduplicatedKeywords={keywords.deduplicated.uniqueKeywords}
                            onCountryToggle={actions.toggleCountry}
                        />
                    ) : null}
                    {state.currentStep === steps.constants.KEYWORDS ? (
                        <KeywordsStep
                            selectedCountries={state.selectedCountries}
                            uniqueKeywords={keywords.deduplicated.uniqueKeywords}
                            hasDuplicates={keywords.deduplicated.hasDuplicates}
                            manualKeywords={keywords.manual}
                            importSources={keywords.imports}
                            onManualInput={actions.setKeywords}
                            onImport={handleKeywordsImport}
                            onRemoveImport={handleRemoveImport}
                        />
                    ) : null}
                    {state.currentStep === steps.constants.CAMPAIGN_NAME ? (
                        <CampaignNameStep
                            campaignName={state.campaignName}
                            onCampaignNameChange={actions.setCampaignName}
                        />
                    ) : null}
                </Modal.Content>
                <WizardFooter>
                    {state.currentStep === steps.constants.COUNTRIES ? (
                        <CountriesStepFooter
                            selectedCountries={state.selectedCountries}
                            onNext={handleCountriesNext}
                            isLoading={keywords.isPrepopulating}
                            selectedDomain={selectedDomain}
                        />
                    ) : state.currentStep === steps.constants.KEYWORDS ? (
                        <KeywordsStepFooter
                            deduplicatedKeywordsCount={keywords.deduplicated.totalCount}
                            onBack={actions.goToPrev}
                            onNext={handleKeywordsNext}
                            selectedDomain={selectedDomain}
                            selectedCountries={state.selectedCountries}
                            isCrawlLimitValid={validation.isCrawlLimitValid}
                        />
                    ) : (
                        <CampaignNameStepFooter
                            isLoading={mutation.isPending || isPending}
                            isValid={validation.isCampaignNameValid ? validation.isCrawlLimitValid : null}
                            onBack={actions.goToPrev}
                            onDone={handleDone}
                        />
                    )}
                </WizardFooter>
            </Modal>
        </>
    );
};
